import { h } from 'preact';
import { accessNested, asList } from 'utils/utils';
import { maybeHandle } from 'utils/gdpr/maybeHandle';
import Store from 'store/store';
import * as actions from 'store/actions';
import { getStaticData } from 'utils/staticData';
import { oneTrust } from 'utils/gdpr';
import formatDateTime from 'utils/formatDateTime';
import { staticPixels } from 'pages/showtimes_features';

let staticData;
const trackedEvents = {};
let trailerOpen;
let timer = Date.now();

function roundVideoProgress(step) {
	return step === 95 ? 100 : step;
}

function isProd() {
	if (typeof window === 'undefined') {
		return false;
	}

	if (__IS_LOCAL__ || __IS_DEMO__ || location.href.includes('stage.showtimes-static')) {
		return false;
	}

	return true;
}

// Renders

// NOTE: Find the staticPixels in src/utils/pixelsGenerator

// GTag things:
// - landingConversion: only once, on landing
// - viewConversion: on each page view, no matter the page
// - pageConversion.pageId: on view for the page pageId
// - ticketsConversion: on ticket click

// Create all the tracking markup that will load before showtimes.js
function createMarkupShowtimes(appData) {
	// let scripts = [];

	if (accessNested(appData, 'options.coppa')) {
		return;
	}

	// Google Tag Manager code
	const gtm = accessNested(appData, 'apis.tracking.googleTagManager', {});
	const uaId = gtm.uaId;
	if (uaId) {
		trackedEvents.gtagLandingConversion = gtm.landingConversion;
		trackedEvents.gtagViewConversion = gtm.viewConversion;
		trackedEvents.gtagTicketsConversion = gtm.ticketConversion;
		trackedEvents.gtagPageConversion = gtm.pageConversion;
		trackedEvents.gtagSocialConversion = gtm.socialConversion;
		trackedEvents.gtagYoutubeConversion = gtm.youtubeConversion;
	}
	// return scripts.length > 1 ? <div>{scripts}</div> : scripts[0];
	initStaticData();
	return;
}

// Static render at the bottom of the file
// Will be loaded after showtimes.js, so don't add anything
// that you might initially need in showtimes
const staticRender = () => <div dangerouslySetInnerHTML={{ __html: staticPixels }} />;

// More dynamic,
// Will render before the showtime.js file (and inside)
// only put it in here if absolutely necessary
const render = ({ appData }) => createMarkupShowtimes(appData);

//
// Other tracking
//

function initStaticData() {
	// Should always be syncronous code
	// Grab the static data
	staticData = getStaticData();

	// Check which events need to be tracked

	// Facebook tracking events
	const fb = accessNested(staticData, 'apis.tracking.pixels.facebookTag', {});
	if (fb && fb.trackTicketClickEvent) {
		// Facebook tracking pixel found, enable it for the tracking
		trackedEvents.fbTicketClick = fb.trackTicketClickEvent.split(',').map(v => v.trim());
	}

	const operam = accessNested(staticData, 'apis.tracking.operam', {});
	if (operam && operam.initId && operam.loadId) {
		// Operam tracking pixel found, enable it for the tracking
		trackedEvents.operam = operam;
	}

	const weborama = accessNested(staticData, 'apis.tracking.pixels.weborama', {});
	trackedEvents.weborama = weborama;
	if (weborama && weborama.landing) {
		const img = new Image();
		img.src = weborama.landing;
	}

	// Remarketing
	let remarketing = accessNested(staticData, 'apis.tracking.pixels.remarketing');
	if (remarketing) {
		remarketing = { ...remarketing };
		if (remarketing.ticket && typeof remarketing.ticket === 'string') {
			const data = remarketing.ticket.split(';').map(e => e.trim());
			remarketing.ticket = { id: data[0], label: data[1] };
		}
		trackedEvents.remarketing = remarketing;
	}

	// Floodlight
	let floodlight = accessNested(staticData, 'apis.tracking.pixels.floodlight');
	if (floodlight) {
		floodlight = { ...floodlight };
		if (floodlight.ticket) {
			const tags = floodlight.ticket.split(',').map(e => {
				if (typeof e !== 'string') {
					return e;
				}
				const val = e.trim().split(':');
				if (!val[0]) return;
				return {
					id: val[0],
					type: val[1] || 'lp',
					cat: val[2] || 'landi0'
				};
			}).filter(e => e);
			floodlight.ticket = tags;
		}
		trackedEvents.floodlight = floodlight;
	}

	// AppNexus
	let appNexus = accessNested(staticData, 'apis.tracking.pixels.appNexus');
	if (appNexus) {
		appNexus = { ...appNexus };
		if (appNexus.ticket && typeof appNexus.ticket === 'string') {
			const data = appNexus.ticket.split(':').map(e => e.trim());
			appNexus.ticket = { id: data[0], seg: data[1] };
		}
		trackedEvents.appNexus = appNexus;
	}

	// Powster Custom GA
	let powsterCustomGA = accessNested(staticData, 'apis.tracking.powsterGATracking');
	if (powsterCustomGA) {
		trackedEvents.powsterCustomGA = asList(powsterCustomGA).filter(id => id.startsWith('UA-') || id.startsWith('G-'));
	}
}

// Fire some operam or puts it in a queue if operam is not ready yet
function fireOperam() {
	if (typeof operam === 'function') {
		// eslint-disable-next-line no-undef
		return operam.apply(this, arguments);
	}
	if (!window.operamQueue) {
		window.operamQueue = [];
	}
	window.operamQueue.push(arguments);
}

function fireGTag(data, externalData = {}) {
	if (typeof gtag !== 'function' || !data) {
		return;
	}
	if (typeof data === 'string') {
		data = { send_to: data };
	}
	if (!data.send_to) {
		return;
	}
	let { send_to, ...params } = data;
	if (params.allow_custom_scripts === undefined) {
		params.allow_custom_scripts = true;
	}
	// Changes { nameVariable: 'u23' } by { u23: externalData.name }
	Object.keys(params).forEach(key => {
		if (key.endsWith('Variable')) {
			const varName = key.slice(0, -'Variable'.length);
			const which = params[key];
			delete params[key];
			if (externalData[varName]) {
				params[which] = externalData[varName];
			}
		}
	});
	// Always add the u1 parameter as the movie name if not something else
	if (params.u1 === undefined) {
		// TODO: cache
		params.u1 = accessNested(Store.get(), 'appData.meta.title.slug');
	}
	if (typeof send_to === 'string') {
		send_to = send_to.split(',');
	} else {
		send_to = asList(send_to);
	}
	// Accept comma-separated values for send_to
	send_to.forEach(tag => {
		if (typeof tag !== 'string') {
			return;
		}
		tag = tag.trim();
		if (!tag) return;
		// console.log('GTAG', { send_to: tag, ...params });
		gtag('event', 'conversion', { send_to: tag, ...params });
	});
}

function firePowsterCustomEvent(action, category, label, options) {
	if (typeof powsterGtag !== 'function') {
		return;
	}
	let tagIds = trackedEvents.powsterCustomGA;

	if (!tagIds) return;

	const store = Store.get();
	const region = accessNested(store, 'appData.meta.region.slug', '');
	const title = accessNested(store, 'appData.meta.title.locale', '');
	const app = title + ' - ' + region.toUpperCase();
	const debug_mode = !isProd();

	const args = {
		event_category: category,
		event_label: label,
		send_to: tagIds,
		event_stream: app,
		allow_google_signals: false,
		region: region,
		debug_mode: debug_mode,
	};

	Object.assign(args, options);

	const powGtagCat = accessNested(store, 'appData.apis.tracking.powsterGATracking.trackingCategory', 'PERFORMANCE');
	maybeHandle(powGtagCat, () => window.powsterGtag('event', action, args), true);
}

function firePowsterTicketClick(screening, theater, ev, provider) {
	const theaterName = theater.name;
	const chain = accessNested(theater, 'exhibitor.domain') || 'X';
	const date = accessNested(screening, 'stDate.ddmmyyyy' || 'X');
	const time = screening.time || 'X';
	const format = accessNested(screening, 'movie.format.name' || 'X');
	if (provider === 'cinema') provider = 'Direct to Cinema';
	const combination = [theaterName, chain, date, time, format, provider].join('_');

	firePowsterCustomEvent('tickets-select-provider', 'tickets', provider);
	firePowsterCustomEvent('tickets-conversion', 'tickets', combination);
}

function firePowsterVideoEvent(isClosableTrailer, videoId, videoType, step) {
	let pageId = Store.get().activePage.id;
	if (pageId === 'showtimes') pageId = 'tickets';
	if (isClosableTrailer) pageId = 'trailer';

	let action = pageId + '-watch';
	let label = videoId + '_' + videoType + '_' + step;

	firePowsterCustomEvent(action, pageId, label);
}

function firePowsterGtagPageView(pageId) {
	if (typeof powsterGtag !== 'function') {
		return;
	}
	let tagIds = trackedEvents.powsterCustomGA;
	if (!tagIds) return;

	const store = Store.get();
	const region = accessNested(store, 'appData.meta.region.slug');

	let path = pageId;
	if (pageId === 'showtimes') {
		path = 'tickets';
	} else if (pageId === 'homeEnt') {
		path = 'homeentstatic';
	} else if (pageId === 'smartHomeEnt') {
		path = 'homeentdynamic';
	}

	const useRawPath = accessNested(store, 'appData.options.trackPathForPageView');

	if (useRawPath) {
		path = accessNested(store, `appData.pages.${pageId}.path`) || pageId;
	}

	const title = accessNested(store, 'appData.meta.title.locale', '');
	const app = title + ' - ' + region.toUpperCase();
	const debug_mode = !isProd();

	const powGtagCat = accessNested(store, 'appData.apis.tracking.powsterGATracking.trackingCategory', 'PERFORMANCE');
	maybeHandle(powGtagCat, () => {
		tagIds.forEach(tagId => {
			window.powsterGtag('config', tagId, {
				page_title: pageId,
				page_path: path,
				anonymize_ip: 'true',
				custom_map: {
					dimension1: 'region'
				},
				event_stream: app,
				allow_google_signals: false,
				region: region,
				debug_mode: debug_mode,
				cookie_flags: (typeof window !== 'undefined' && window !== window.top) ? 'SameSite=None; Secure' : ''
			});
		});
	}, true);

	if (pageId === 'gallery') {
		Store.emit(actions.GALLERY_VIEW_IMAGE, 1);
	}
}

function firePowsterGtagLoad() {
	const loaded = Date.now();
	const loadTime = loaded - timer;
	firePowsterCustomEvent('timing_complete', 'pageLoad', '', { name: 'load', value: loadTime });
}

function firePageView(page) {
	const pageId = page && page.id;

	if (trackedEvents.operam) {
		fireOperam('plugin', 'powster1', 'pageview');
	}
	if (trackedEvents.gtagViewConversion) {
		fireGTag(trackedEvents.gtagViewConversion, {
			url: location.href
		});
	}

	// 11/3/20: Added same hack from PAGE_STATE_LOADED
	// Believe fireGtag is firing before page is loaded and using location.href
	// from prev page, so added setTimeout
	setTimeout(() => {
		if (trackedEvents.gtagViewConversion) {
			fireGTag(trackedEvents.gtagViewConversion, {
				url: location.href
			});
		}
	}, 0);

	const currentPageGTag = trackedEvents.gtagPageConversion && trackedEvents.gtagPageConversion[pageId];
	if (currentPageGTag) {
		fireGTag(currentPageGTag);
	}
}

const storeListeners = {};
storeListeners[actions.PAGE_STATE_LOADED] = () => {
	if (!staticData) {
		initStaticData();
	}
	// staticData exists
	// trackedEvents filled with all the events that we need
	setTimeout(() => {
		if (trackedEvents.gtagLandingConversion) {
			fireGTag(trackedEvents.gtagLandingConversion, {
				url: location.href
			});
		}
		firePageView(Store.get().activePage);
		firePowsterGtagPageView(Store.get().activePage?.id);
	}, 0);
	// ^ setTimeout as a ugly hack. If we don't do that with onetrust enabled, the gtag func is not available yet - needs a render
	if (document.readyState === 'complete') {
		firePowsterGtagLoad();
	} else {
		window.addEventListener('load', firePowsterGtagLoad);
	}
};

storeListeners[actions.SET_ACTIVE_PAGE] = (activePage) => {
	firePageView(activePage);
	firePowsterGtagPageView(activePage.id);
};

storeListeners[actions.TICKET_LINK_CLICKED] = (screening, theater, ev, provider = 'cinema') => {
	if (trackedEvents.fbTicketClick && typeof fbq === 'function') {
		const store = Store.get();

		const eventDetails = {
			film_name: accessNested(store, 'appData.meta.title.slug'),
			cinema_name: theater.name,
			time_slot: screening.time24,
			cta: undefined
		};

		// Add screeening CTA, move this up if you need it for all pixels
		if (typeof screening.cta !== 'undefined') {
			const ctaCopy = accessNested(store, 'appData.pages.showtimes.data.ctaCopy');
			const copy = accessNested(store, 'appData.copy');
			if (ctaCopy && typeof ctaCopy === 'object' && copy && typeof copy === 'object') {
				const cta = copy[ctaCopy[screening.cta]];
				if (typeof cta === 'string' && cta.length > 0) {
					eventDetails.cta = cta;
				}
			}
		}

		trackedEvents.fbTicketClick.forEach(eventName => {
			let eventType = 'trackCustom';

			const isStandardEvent = [
				'InitiateCheckout',
				'AddToCart',
				'ViewContent',
				'AddPaymentInfo',
				'Purchase',
				'Lead'
			].indexOf(eventName) !== -1;
			if (isStandardEvent) {
				eventType = 'track';
			}

			// Track the facebook event
			fbq(eventType, eventName, eventDetails); // TODO: Cache
		});
	}

	if (trackedEvents.operam) {
		fireOperam('plugin', 'powster1', 'buy_ticket', {
			city: theater.location.city,
			theater_name: theater.name,
			theater_address: theater.location.fullAddress,
			time: screening.time24,
			date: screening.date,
			type: screening.movie.format.name,
			provider: provider
		});
	}

	if (trackedEvents.gtagTicketsConversion) {
		fireGTag(trackedEvents.gtagTicketsConversion, {
			url: location.href,
			theaterUrl: accessNested(theater, 'exhibitor.domain') || '',
			theater: theater.name,
			location: theater.location.fullAddress,
			date: screening.date.slice(0, 10),
			time: screening.time,
			provider: provider,
		});
	}

	if (trackedEvents.weborama && trackedEvents.weborama.ticket) {
		const weboramaPixel = () => {
			const img = new Image();
			img.src = trackedEvents.weborama.ticket;
		};

		const weboramaOneTrustCategory = trackedEvents.weborama.oneTrustCategory;
		oneTrust.maybeHandle(weboramaOneTrustCategory, weboramaPixel, true);
	}

	if (trackedEvents.remarketing && trackedEvents.remarketing.ticket && trackedEvents.remarketing.ticket.id) {
		const remarketingPixel = () => {
			const img = new Image();
			let url = '//www.googleadservices.com/pagead/conversion/' + trackedEvents.remarketing.ticket.id + '/';
			if (trackedEvents.remarketing.ticket.label) {
				url += '?label=' + trackedEvents.remarketing.ticket.label;
			}
			img.src = url;
		};

		const remarketingOneTrustCategory = trackedEvents.remarketing.oneTrustCategory;
		oneTrust.maybeHandle(remarketingOneTrustCategory, remarketingPixel, true);
	}

	if (trackedEvents.floodlight && trackedEvents.floodlight.ticket && trackedEvents.floodlight.ticket.length) {
		const floodlightPixel = () => {
			trackedEvents.floodlight.ticket.forEach(tag => {
				const iframe = document.createElement('iframe');
				const rand = (Math.random() + '').slice(2);
				iframe.src = `https://${tag.id}.fls.doubleclick.net/activityi;src=${tag.id};type=${tag.type};cat=${tag.cat};dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;ord=${rand}?`;
				iframe.style.display = 'none';
				document.body.appendChild(iframe);
			});
		};

		const floodlightOneTrustCategory = trackedEvents.floodlight.oneTrustCategory;
		oneTrust.maybeHandle(floodlightOneTrustCategory, floodlightPixel, true);
	}

	if (trackedEvents.appNexus && trackedEvents.appNexus.ticket && trackedEvents.appNexus.ticket.id) {
		const appNexusPixel = () => {
			const img = new Image();
			let url = '//secure.adnxs.com/px?id=' + trackedEvents.appNexus.ticket.id + '&seg=' + trackedEvents.appNexus.ticket.seg + '&t=2';
			img.src = url;
		};

		const appNexusOneTrustCategory = trackedEvents.appNexus.oneTrustCategory;
		oneTrust.maybeHandle(appNexusOneTrustCategory, appNexusPixel, true);
	}

	firePowsterTicketClick(screening, theater, ev, provider);
};

storeListeners[actions.SEARCH_NEW_CITY] = (cityName) => {
	if (trackedEvents.operam) {
		fireOperam('plugin', 'powster1', 'search', { city: cityName });
	}

	firePowsterCustomEvent('tickets-search', 'tickets', '-');
};

storeListeners[actions.SELECT_SCREENING] = ({ theater, screening }) => {
	if (trackedEvents.operam) {
		fireOperam('plugin', 'powster1', 'select_time', {
			city: theater.location.city,
			theater_name: theater.name,
			theater_address: theater.location.fullAddress,
			time: screening.time24,
			date: screening.date,
			type: screening.movie.format.name
		});
	}
};

storeListeners[actions.CLICKED_SHARE] = (id) => {
	if (trackedEvents.gtagSocialConversion) {
		fireGTag(trackedEvents.gtagSocialConversion, { platform: id });
	}
};

storeListeners[actions.YOUTUBE_PLAY] = ({ id }) => {
	if (trackedEvents.gtagYoutubeConversion) {
		fireGTag(trackedEvents.gtagYoutubeConversion, { video: id });
	}
};

storeListeners[actions.CALENDAR_CLICKED] = ({ title }, activePage) => {
	let action = 'calendar';
	let category = 'calendar-reminder';
	if (activePage === 'showtimes' || activePage === 'tickets') {
		action = 'tickets';
		category = 'tickets';
	}
	firePowsterCustomEvent(action + '-reminder_click', category, title);
};

storeListeners[actions.HOME_BUTTON_CLICKED] = (label) => {
	firePowsterCustomEvent('button-click', 'home', label);
};

storeListeners[actions.GMAPS_LOADED] = () => {
	firePowsterCustomEvent('map-interact', 'map', '-');
};

storeListeners[actions.MAP_REQUEST_DIRECTIONS] = (travelMode) => {
	firePowsterCustomEvent('map-request_direction', 'map', travelMode.toLowerCase());
};

storeListeners[actions.GALLERY_VIEW_IMAGE] = (index) => {
	firePowsterCustomEvent('gallery-view_image', 'gallery', index);
};

storeListeners[actions.GALLERY_VIEW_IMAGE_FULLSCREEN] = (index) => {
	firePowsterCustomEvent('gallery-view_enlarge_image', 'gallery', index);
};

storeListeners[actions.GALLERY_DOWNLOAD_IMAGE] = (index) => {
	firePowsterCustomEvent('gallery-image_download', 'gallery', index);
};

storeListeners[actions.YOUTUBE_PROGRESS] = (step, ytid, length, data, isClosableTrailer) => {
	if (step !== 100) {
		firePowsterVideoEvent(isClosableTrailer, ytid, 'Youtube', roundVideoProgress(step));
	}
};

storeListeners[actions.VIDEO_PLAYER_PROGRESS] = (step, name, isClosableTrailer) => {
	if (step !== 100) {
		const splitName = name.split('.');
		firePowsterVideoEvent(isClosableTrailer, splitName[0], splitName[1], roundVideoProgress(step));
	}
};

storeListeners[actions.JW_PLAYER_PROGRESS] = (step, name, isClosableTrailer) => {
	if (step !== 100) {
		firePowsterVideoEvent(isClosableTrailer, name, 'JWPlayer', roundVideoProgress(step));
	}
};

storeListeners[actions.VIMEO_VIDEO_PROGRESS] = (step, name, isClosableTrailer) => {
	firePowsterVideoEvent(isClosableTrailer, name, 'Vimeo', step);
};

storeListeners[actions.VIDEOS_TICKET_CLICK] = () => {
	firePowsterCustomEvent('videos-get_tickets', 'videos', '-');
};

storeListeners[actions.CLOSABLE_TRAILER_OPEN] = () => {
	trailerOpen = new Date();
	firePowsterGtagPageView('trailer');
};

storeListeners[actions.CLOSABLE_TRAILER_CLOSED] = (page) => {
	const trailerClosed = new Date();
	const duration = Math.floor((trailerClosed - trailerOpen) / 1000);

	const pageId = page && page.id;

	firePowsterCustomEvent('trailer-bounce', 'trailer', duration);
	firePowsterGtagPageView(pageId);
};

storeListeners[actions.CLICKED_SHARE] = (name) => {
	firePowsterCustomEvent('social-share', 'social', name);
};

storeListeners[actions.CLICKED_FOLLOW] = (name) => {
	firePowsterCustomEvent('social-follow', 'social', name);
};

storeListeners[actions.HOMEENT_VENDOR_CLICKED] = (url, name, altCategory, pageId) => {
	let vendorName;
	const useTrackingPrefixForGA = accessNested(Store.get(), 'activePage.data.useTrackingPrefixForGA');
	if (url) {
		url = new URL(url);
		vendorName = url.hostname.replace('www.', '').replace('.com', '');
	} else if (pageId) {
		vendorName = '/' + pageId.toLowerCase();
	}

	if (url && useTrackingPrefixForGA) {
		vendorName = name; // 09-24-20: When url domain names are all the same, but vendor names are unique
	}

	let category = altCategory || 'homeent_s';

	firePowsterCustomEvent(category + '-merchant_select', category, vendorName);
};

storeListeners[actions.SET_CITIES] = () => {
	const theaters = accessNested(Store.get(), 'showtimes.theaters');
	if (theaters && theaters.length) {
		const name = theaters[0].name || 'X';
		const domain = accessNested(theaters[0], 'exhibitor.domain', 'X');
		firePowsterCustomEvent('tickets-focus-theatre-chain', 'tickets', name + '_' + domain);
	}
};

storeListeners[actions.SELECT_THEATER] = (id, origin, data) => {
	const name = data.name || 'X';
	const domain = accessNested(data, 'exhibitor.domain', 'X');
	firePowsterCustomEvent('tickets-select-theatre-chain', 'tickets', name + '-' + domain);
};

storeListeners[actions.SELECT_ST_DATE] = ({ date, ddmmyyyy }) => {
	firePowsterCustomEvent('tickets-select-calendar_date', 'tickets', ddmmyyyy);
	firePowsterCustomEvent('tickets-select-calendar_day', 'tickets', formatDateTime(date, 'ddd'));
};

storeListeners[actions.SELECT_TIME] = (theater, screening) => {
	const format = accessNested(screening, 'movie.format.name');
	const timezone = screening.datetime ? screening.datetime.toString().match(/([A-Z]+[+-][0-9]+)/)[1] : '';
	firePowsterCustomEvent('tickets-select-calendar_time', 'tickets', screening.time + '_' + timezone);
	firePowsterCustomEvent('tickets-select-format', 'tickets', format);
};

storeListeners[actions.SET_CLIENT_GEO] = ({ city, countryCode }) => {
	let pageId = Store.get().activePage.id;
	if (pageId === 'tickets' || pageId === 'showtimes') {
		firePowsterCustomEvent('tickets-filter_location', 'tickets', city + '_' + countryCode.toUpperCase());
	}
};

storeListeners[actions.TOGGLE_FILTER] = (filter, options) => {
	if (options && options.length) {
		let label; let action;
		switch (filter) {
			case 'stDate':
				action = 'day';
				label = options[0].ddmmyyyy;
				break;
			case 'timeOfDay':
				action = 'time';
				label = options[0].id && options[0].id.substring(1).toLowerCase();
				break;
			case 'format':
				action = 'format';
				label = options[0].label;
		}
		firePowsterCustomEvent('tickets-filter_' + action, 'tickets', label);
	}
};

storeListeners[actions.SET_THEATERS] = (theaters, error, geo) => {
	if (error && error === 'BOUNDING_REGION') {
		firePowsterCustomEvent('tickets-redirect_origin', 'tickets', geo && geo.countryCode);
	} else if (error || !geo) {
		firePowsterCustomEvent('tickets-error', 'tickets', '-');
	} else if (!theaters || theaters.length === 0) {
		firePowsterCustomEvent('tickets-no_showtimes', 'tickets', geo && geo.city);
	}
};

storeListeners[actions.SMART_HOMEENT_SELECT] = ({ format, availability, provider }) => {
	if (format) {
		firePowsterCustomEvent('homeent_d-format_select', 'homeent_d', format);
	}
	if (availability) {
		firePowsterCustomEvent('homeent_d-availability_select', 'homeent_d', availability);
	}
	if (provider) {
		firePowsterCustomEvent('homeent_d-merchant_select', 'homeent_d', provider.replace(/ /g, '').toLowerCase());
	}
};

storeListeners[actions.SMART_HOMEENT_LINK_CLICKED] = (data) => {
	const { format, availability, provider, edition, price, dataProvider } = data;
	let label = [format, availability, provider, edition, price]
		.join('_');
	firePowsterCustomEvent('homeent_d-provider', 'homeent_d', dataProvider);
	firePowsterCustomEvent('homeent_d-conversion', 'homeent_d', label);
};

storeListeners[actions.API_ERROR] = (api, error) => {
	firePowsterCustomEvent('exception', '', '', { description: api + ' - ' + error, fatal: false });
};

storeListeners[actions.POWSTER_GA_EVENT_TRACK] = (action, category, label, options) => {
	firePowsterCustomEvent(action, category, label, options);
};

storeListeners[actions.POWSTER_GA_PAGE_VIEW_TRACK] = (pageId) => {
	firePowsterGtagPageView(pageId);
};

// SCROLLSITE
storeListeners[actions.SCROLLSITE_BUTTON_CLICK] = (section, title, destination) => {
	const activePage = accessNested(Store.get(), 'activePage.id');
	const label = title + '_' + destination;
	firePowsterCustomEvent(section, activePage, label);
};

storeListeners[actions.SCROLLSITE_GALLERY_ITEM_CLICK] = (section, title, filename) => {
	const activePage = accessNested(Store.get(), 'activePage.id');
	const label = title ? (title + '_' + filename.split('/').pop()) : filename.split('/').pop();
	firePowsterCustomEvent(section, activePage, label);
};

storeListeners[actions.SCROLLSITE_RESOURCE_CLICK] = (section, title, name, destination) => {
	if (destination) {
		const activePage = accessNested(Store.get(), 'activePage.id');
		const label = (title ? title + '_' : '') + name + '_' + destination;
		firePowsterCustomEvent(section, activePage, label);
	}
};

export { staticRender, render, storeListeners };
